import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  COMPLETION_STEP,
  type NumberRequestCompletion,
} from "@/features/number-requests/completion";
import {
  NUMBER_REQUEST_STEPS,
  type NumberRequestKind,
  type NumberRequestState,
} from "@/features/number-requests/types";
import { useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import { CheckIcon } from "@phosphor-icons/react";

/**
 * "What is actually going to happen to me" — the single most asked question
 * about a number request, and the one the state machine answers badly: a client
 * reading "submitted" has no idea whether they still have to do something.
 *
 * So this walks the *human* procedure (4 steps, different per kind) rather than
 * the 6 internal states, and marks how far the request has got by mapping each
 * human step onto the states it covers. Same component for both kinds — only
 * the copy and the mapping differ.
 */

/**
 * Which internal state each human step is "done" by. A step is complete once
 * the request has reached the state listed here.
 *
 * All three end with a live number, so the last entry matches; everything
 * before it is where the processes diverge. `sip_trunk` has no documents to
 * review, so its first step is done as soon as the request leaves `draft`.
 *
 * `port_existing` maps like `new_number` because it *is* one: we provision a
 * number and ship hardware, and what the client does with their old line
 * afterwards (plug in, test, forward) happens on their own clock, once the
 * request is `provisioned`. The steps read differently; the states don't.
 */
const STEP_COMPLETED_AT: Record<NumberRequestKind, NumberRequestState[]> = {
  new_number: ["documents_review", "approved", "provisioned", "provisioned"],
  port_existing: ["documents_review", "approved", "provisioned", "provisioned"],
  sip_trunk: ["documents_pending", "submitted", "approved", "provisioned"],
};

const STEP_COUNT = 4;

export function ProcessSteps({
  kind,
  state,
  guided,
  completion,
}: {
  kind: NumberRequestKind;
  state: NumberRequestState;
  /**
   * Opened through the guided creation, where the client's step (papers, or
   * describing a SIP setup) was the dialog itself — its copy says so.
   */
  guided?: boolean;
  /**
   * How far the client is through the blocks on the left. Shown on the one
   * step that is theirs (`COMPLETION_STEP`), and only while it is still open:
   * once we have everything, the step is simply ticked.
   */
  completion?: NumberRequestCompletion;
}) {
  const { t } = useTranslation("numberRequests");

  // Where the request stands on the happy path. Exits (rejected/cancelled) are
  // not on it, so nothing is marked done — which is the honest answer.
  const reached = NUMBER_REQUEST_STEPS.indexOf(state);

  const isDone = (index: number) => {
    if (reached < 0) return false;
    const completedAt = STEP_COMPLETED_AT[kind][index];
    return reached >= NUMBER_REQUEST_STEPS.indexOf(completedAt);
  };

  // The first step that isn't done yet — the one the client should be reading.
  const currentIndex = Array.from({ length: STEP_COUNT }).findIndex(
    (_, index) => !isDone(index),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("numberRequests.process.title")}</CardTitle>
        <p className="text-muted-foreground text-sm">
          {t(`numberRequests.process.${kind}.intro`)}
        </p>
      </CardHeader>
      <CardContent>
        <ol className="space-y-4">
          {Array.from({ length: STEP_COUNT }).map((_, index) => {
            const done = isDone(index);
            const current = index === currentIndex;

            return (
              <li key={index} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span
                    className={cn(
                      "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-medium tabular-nums",
                      done &&
                        "bg-primary text-primary-foreground border-primary",
                      current && !done && "border-primary text-primary",
                      !done && !current && "text-muted-foreground",
                    )}
                  >
                    {done ? (
                      <CheckIcon className="h-4 w-4" weight="bold" />
                    ) : (
                      index + 1
                    )}
                  </span>
                  {index < STEP_COUNT - 1 && (
                    <span
                      className={cn(
                        "mt-1 w-px flex-1",
                        done ? "bg-primary" : "bg-border",
                      )}
                    />
                  )}
                </div>

                <div
                  className={cn(
                    "min-w-0 flex-1 pb-1",
                    !done && !current && "opacity-70",
                  )}
                >
                  <p className="text-sm font-medium">
                    {t(
                      guided && index === 0
                        ? `numberRequests.process.guided.${kind}.title`
                        : `numberRequests.process.${kind}.steps.${index}.title`,
                    )}
                  </p>
                  <p className="text-muted-foreground text-sm">
                    {t(
                      guided && index === 0
                        ? `numberRequests.process.guided.${kind}.body`
                        : `numberRequests.process.${kind}.steps.${index}.body`,
                    )}
                  </p>

                  {completion &&
                    index === COMPLETION_STEP &&
                    completion.total > 0 &&
                    !done && (
                      <div className="mt-3 space-y-1.5">
                        <div className="flex items-baseline justify-between gap-2 text-xs">
                          <span className="font-medium">
                            {t("numberRequests.process.completion.label")}
                          </span>
                          <span className="text-muted-foreground tabular-nums">
                            {t("numberRequests.process.completion.value", {
                              percent: completion.percent,
                              done: completion.done,
                              total: completion.total,
                            })}
                          </span>
                        </div>
                        <div
                          className="bg-muted h-1.5 w-full overflow-hidden rounded-full"
                          role="progressbar"
                          aria-valuenow={completion.percent}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        >
                          <div
                            className="bg-primary h-full rounded-full transition-all"
                            style={{ width: `${completion.percent}%` }}
                          />
                        </div>
                        <p className="text-muted-foreground text-xs">
                          {completion.complete
                            ? t("numberRequests.process.completion.complete")
                            : t("numberRequests.process.completion.remaining", {
                                count: completion.total - completion.done,
                              })}
                        </p>
                      </div>
                    )}
                </div>
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}
